import React, { useState, useEffect, useContext, useCallback } from 'react';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import ArgonBox from 'components/ArgonBox';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import companyInfo from 'context/companyInfo';
import OrgTable from './components/OrgTable';
import OrgSetup from './components/OrgSetup';
import { userOrgs, deleteOrg } from 'services/org';
import { UserContext } from 'context/userContext';
import { useNotifications } from 'context/NotificationContext';

function Tables() {
  const [showOrgSetup, setShowOrgSetup] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const { userId } = useContext(UserContext);
  const { addNotification } = useNotifications();

  const refreshOrgs = useCallback(async () => {
    if (!userId) {
      setOrgs([]);
      return;
    }
    try {
      const response = await userOrgs(userId);
      console.log('ORGS', response);
      if (response?.orgs) {
        setOrgs(response.orgs);
      } else {
        setOrgs([]);
        addNotification({
          // Directly notify
          title: ['No Organizations'],
          details: 'No organizations found for this user.',
          severity: 'info', // Or 'warning'
        });
      }
    } catch (error) {
      console.error('Error fetching orgs:', error);
      addNotification({
        title: ['Error Loading Orgs'],
        details: error.message || 'Could not retrieve your organization list.',
        severity: 'error',
      });
      setOrgs([]);
    }
  }, [userId, addNotification]);

  useEffect(() => {
    refreshOrgs();
  }, [refreshOrgs]);

  const handleAddNewOrg = useCallback(() => {
    setSelectedOrg(null);
    setShowOrgSetup(true);
    addNotification({
      title: ['New Organization'],
      details: 'Opening form to add a new Salesforce organization.',
      severity: 'info',
    });
  }, [addNotification]);

  const handleEditOrg = useCallback(
    (org) => {
      setSelectedOrg(org);
      setShowOrgSetup(true);
      addNotification({
        // Optional: Add notification for editing
        title: ['Editing Organization', org.name || ''],
        details: 'Loading organization details for editing.',
        severity: 'info',
      });
    },
    [addNotification]
  );

  const handleCloseOrgSetup = useCallback(() => {
    setShowOrgSetup(false);
    setSelectedOrg(null);
    // If OrgSetup successfully saves, it calls refreshOrgs.
    // If you want to notify on cancel, you could add an info notification here.
    // e.g., addNotification({ title: ['Setup Closed'], details: 'Organization setup was closed.', severity: 'info' });
  }, []); // addNotification could be a dependency if used here

  const handleDeleteOrg = useCallback(
    async (orgId) => {
      // Find the org name for a more informative notification, if possible
      const orgToDelete = orgs.find((org) => org.org_id === orgId);
      const orgName = orgToDelete ? orgToDelete.name : 'the organization';

      addNotification({
        title: ['Deleting Organization', orgToDelete ? orgToDelete.name : ''],
        details: `Attempting to remove ${orgName}...`,
        severity: 'info',
      });

      try {
        await deleteOrg(orgId);
        addNotification({
          title: ['Organization Deleted', orgToDelete ? orgToDelete.name : ''],
          details: `${orgName} was successfully removed.`,
          severity: 'success',
        });
        refreshOrgs();
      } catch (error) {
        console.error('Error deleting org:', error);
        addNotification({
          title: ['Deletion Failed', orgToDelete ? orgToDelete.name : ''],
          details: `Could not delete ${orgName}. ${error.message || 'Please try again.'}`,
          severity: 'error',
        });
      }
    },
    [addNotification, refreshOrgs, orgs] // Added orgs to dependencies to get orgName
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        {!showOrgSetup && (
          <>
            <ArgonBox
              mb={3}
              mt={2}
              display="flex"
              justifyContent="flex-end"
              sx={{ position: 'absolute', right: '3rem', zIndex: 2 }}
            >
              <Button variant="contained" color="info" onClick={handleAddNewOrg}>
                Add New Org
              </Button>
            </ArgonBox>
            <OrgTable orgs={orgs} onEdit={handleEditOrg} onDelete={handleDeleteOrg} refreshOrgs={refreshOrgs} />
          </>
        )}

        {showOrgSetup && (
          <ArgonBox>
            <Card>
              <OrgSetup orgData={selectedOrg} onClose={handleCloseOrgSetup} refreshOrgs={refreshOrgs} />
            </Card>
          </ArgonBox>
        )}
      </ArgonBox>
      <Footer company={companyInfo} links={companyInfo.links} />
    </DashboardLayout>
  );
}

export default Tables;
